import { useEffect, useState } from 'react';
import { useDebounce } from 'usehooks-ts';
import { DEBOUNCE_TIME_IN_MS, MACRO_SEARCH_URL } from '@constants';
import { useAuthHttp } from './useHttp';
import { MacroSearchResponse, MacroSearchResult } from './types';

const MINIMUM_SEARCH_LENGTH = 3;
export const MAXIMUM_SEARCH_LENGTH = 120;

type FinishedSearch = {
  searchQuery: string;
  results: MacroSearchResult[];
  hasFailed: boolean;
};

export const useMacroSearch = (searchText: string) => {
  const authHttp = useAuthHttp();
  const trimmedSearchText = searchText.trim();
  const searchQuery = useDebounce(trimmedSearchText, DEBOUNCE_TIME_IN_MS);
  const [finishedSearch, setFinishedSearch] = useState<FinishedSearch | null>(
    null
  );

  useEffect(() => {
    if (searchQuery.length < MINIMUM_SEARCH_LENGTH) return undefined;

    const request = new AbortController();
    let isLatestSearch = true;

    const finishSearch = (results: MacroSearchResult[], hasFailed: boolean) => {
      if (isLatestSearch) {
        setFinishedSearch({ searchQuery, results, hasFailed });
      }
    };

    authHttp
      .get<MacroSearchResponse>(MACRO_SEARCH_URL, {
        params: { searchQuery },
        signal: request.signal,
      })
      .then(({ data }) => finishSearch(data.results ?? [], false))
      .catch(() => finishSearch([], true));

    return () => {
      isLatestSearch = false;
      request.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  const isLongEnoughToSearch =
    trimmedSearchText.length >= MINIMUM_SEARCH_LENGTH;
  const searchOfThisText =
    isLongEnoughToSearch && finishedSearch?.searchQuery === trimmedSearchText
      ? finishedSearch
      : null;

  return {
    isLongEnoughToSearch,
    isSearching: isLongEnoughToSearch && !searchOfThisText,
    results: searchOfThisText?.results ?? [],
    hasFailed: searchOfThisText?.hasFailed ?? false,
  };
};
